import type { VehicleEntity } from '../game/entities';
import type { Camera } from './camera';
import type { WorldStream } from './world-stream';

/**
 * (track 2, owner Sep 11) What a vehicle leaves on the ground, by the ground: rubber only where rubber makes sense
 * (asphalt, pavement, concrete), a muddy trail on grass and soil, a rut in sand, a track in snow, and on a dirt road
 * or sand a puff of dust that settles. Everything here is the client's own paint — nothing goes on the wire — laid
 * from the vehicles it can see and kept for a while.
 */

export type MarkKind = 'rubber' | 'mud' | 'sand' | 'snow';
export interface Mark { x0: number; y0: number; x1: number; y1: number; t: number; k: number; kind: MarkKind }
export interface Dust { x: number; y: number; vx: number; vy: number; t: number; life: number; r: number; rgb: string }
export interface Surface {
  /** the permanent mark this ground takes, if any */
  mark?: MarkKind;
  /** how strongly a wheel that merely rolls over it marks it (0: only a skid leaves rubber) */
  trail: number;
  /** the dust it throws up, as an "r,g,b" string, or none */
  dust?: string;
}

/** how a mark is drawn: its colour, its width in tiles, how long it lasts (s) and how dark it starts */
export const MARK_STYLE: Record<MarkKind, { rgb: string; width: number; life: number; alpha: number }> = {
  rubber: { rgb: '18,16,16', width: 0.22, life: 120, alpha: 0.5 },
  mud: { rgb: '58,40,22', width: 0.3, life: 240, alpha: 0.42 },
  sand: { rgb: '128,106,64', width: 0.3, life: 90, alpha: 0.3 },
  snow: { rgb: '118,136,166', width: 0.32, life: 180, alpha: 0.4 },
};

/** grass and soil take mud; sand a rut; snow a track; man-made ground rubber; rock, water and gravel nothing that lasts */
const BY_TERRAIN: Record<string, Surface> = {
  grass: { mark: 'mud', trail: 0.5 }, lawn: { mark: 'mud', trail: 0.5 }, forest: { mark: 'mud', trail: 0.6 }, farmland: { mark: 'mud', trail: 0.7 },
  scrub: { mark: 'mud', trail: 0.35, dust: '176,160,120' }, tundra: { mark: 'mud', trail: 0.4 }, marsh: { mark: 'mud', trail: 0.9 },
  sand: { mark: 'sand', trail: 0.6, dust: '222,206,150' }, desert_sand: { mark: 'sand', trail: 0.6, dust: '228,204,130' },
  snow: { mark: 'snow', trail: 0.7 }, pavement: { mark: 'rubber', trail: 0 }, gravel: { trail: 0, dust: '182,176,164' }, rock: { trail: 0 },
};
const NONE: Surface = { trail: 0 };
const DIRT_ROAD: Surface = { trail: 0, dust: '170,150,116' };
const PAVED_ROAD: Surface = { mark: 'rubber', trail: 0 };

/** the ground under a point: a road first (its class says what it is made of), else the terrain */
export function surfaceAt(w: WorldStream, x: number, y: number): Surface {
  const road = w.roadAt(x, y);
  if (road) return road.class === 'rail' ? NONE : road.class === 'dirt' ? DIRT_ROAD : PAVED_ROAD;
  const t = w.terrainAt(x, y);
  return t ? BY_TERRAIN[t.id] ?? NONE : NONE;
}

/** a puff behind a wheel: it drifts back the way the car came, grows and thins, then it has settled */
export function puff(list: Dust[], x: number, y: number, fx: number, fy: number, speed: number, rgb: string, rnd: number, rnd2: number): void {
  const spread = 0.5 + Math.min(1, speed / 20);
  list.push({ x: x + (rnd - 0.5) * 0.6, y: y + (rnd2 - 0.5) * 0.6, vx: -fx * 0.8 + (rnd2 - 0.5) * 0.6, vy: -fy * 0.8 + (rnd - 0.5) * 0.6, t: 0, life: 1.3 + rnd * 0.9, r: 0.35 * spread, rgb });
}

/** step every puff and draw the ones in view, oldest first; a settled puff is dropped */
export function stepAndDrawDust(list: Dust[], ctx: CanvasRenderingContext2D, cam: Camera, dt: number, vb: { x0: number; y0: number; x1: number; y1: number }): void {
  let w = 0;
  for (let i = 0; i < list.length; i++) {
    const d = list[i]; d.t += dt;
    if (d.t >= d.life) continue;
    d.x += d.vx * dt; d.y += d.vy * dt; d.vx *= 1 - 1.5 * dt; d.vy *= 1 - 1.5 * dt;
    list[w++] = d;
  }
  list.length = w;
  const s = cam.scale;
  for (const d of list) {
    if (d.x < vb.x0 - 2 || d.x > vb.x1 + 2 || d.y < vb.y0 - 2 || d.y > vb.y1 + 2) continue;
    const k = d.t / d.life, r = (d.r + k * 1.1) * s;
    const [sx, sy] = cam.toScreen(d.x, d.y);
    ctx.fillStyle = `rgba(${d.rgb},${(0.32 * (1 - k) * (1 - k)).toFixed(3)})`;
    ctx.beginPath(); ctx.arc(sx, sy, r, 0, Math.PI * 2); ctx.fill();
  }
}

/** the rear axle's two wheel points (one for a two-wheeler), where marks are laid and dust rises */
export function rearWheels(v: VehicleEntity): { l: [number, number]; r: [number, number]; single: boolean; fx: number; fy: number } {
  const single = v.def.class === 'bike' && v.def.size[0] < 1;
  const fx = Math.sin(v.angle), fy = -Math.cos(v.angle), rx = -fy, ry = fx, back = v.def.size[1] * 0.32, half = single ? 0 : v.def.size[0] * 0.38;
  const ax = v.x - fx * back, ay = v.y - fy * back;
  return { l: [ax - rx * half, ay - ry * half], r: [ax + rx * half, ay + ry * half], single, fx, fy };
}
